// Thin, typed wrapper around Google Analytics 4 (gtag.js).
//
// GA4 is bootstrapped in index.html (the gtag.js snippet for G-GF9YJMDXCL).
// Here we forward our own custom events via gtag('event', …). Everything
// degrades to a no-op when gtag isn't present (SSR, ad-blockers, consent not
// granted) — analytics must never break the app.

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

/** Send a custom GA4 event. Safe no-op if gtag is unavailable. */
export function pushEvent(event: string, params: Record<string, unknown> = {}): void {
  try {
    if (typeof window === "undefined") return;
    if (typeof window.gtag === "function") {
      window.gtag("event", event, params);
    } else {
      // Fallback : queue on the dataLayer so a late-loading gtag still picks it up.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(["event", event, params]);
    }
  } catch {
    /* analytics is best-effort — swallow everything */
  }
}

/**
 * Virtual page view for a SPA. gtag is configured with send_page_view:false,
 * so this is the single source of page views — fired on the initial load and
 * on every React Router navigation (see RouteTracker).
 */
export function trackPageView(path: string, title?: string): void {
  pushEvent("page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : path,
    page_title: title ?? (typeof document !== "undefined" ? document.title : undefined),
  });
}

// ── Domain events ──
// Named, intention-revealing helpers so call sites read clearly and the event
// vocabulary stays consistent (GTM/GA4 group on the `event` name + params).

export const analytics = {
  /** A user toggled follow on a forum / user / thread. */
  toggleFollow: (targetType: "forum" | "user" | "thread", action: "follow" | "unfollow", id: number | string) =>
    pushEvent("toggle_follow", { target_type: targetType, follow_action: action, target_id: String(id) }),

  /** A refresh/scrape was requested (forum price, thread, user, top prices…). */
  refresh: (kind: string, id?: number | string) =>
    pushEvent("refresh_request", { refresh_kind: kind, target_id: id != null ? String(id) : undefined }),

  /** A summarize / AI analysis was launched. */
  summarize: (kind: "forum" | "thread", id: number | string) =>
    pushEvent("summarize", { summarize_kind: kind, target_id: String(id) }),

  /** An outbound link to Boursorama was opened. */
  outboundBoursorama: (kind: "forum" | "thread" | "user", id?: number | string) =>
    pushEvent("outbound_boursorama", { outbound_kind: kind, target_id: id != null ? String(id) : undefined }),

  /** The upgrade / Pro dialog was opened. */
  upgradeOpen: (source?: string) => pushEvent("upgrade_open", { source }),

  /** Sign-in flow milestones (OTP requested / verified). */
  authStep: (step: "otp_requested" | "otp_verified" | "sign_out") => pushEvent("auth", { auth_step: step }),
};
